<template>
  <div id="accordionTourOptional" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingTourOptional" class="accordion-header">
        <button
          aria-controls="collapseTourOptional"
          aria-expanded="true"
          class="accordion-button collapsed w-100 d-flex justify-content-between align-items-center"
          data-bs-target="#collapseTourOptional"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex align-items-center">
            <div class="bg-primary rounded-xs me-8 d-flex align-items-center justify-content-center" style="width: 24px; height: 24px; flex: none;">
              <v-icon icon="contact" size="xs" class="text-white" />
            </div>

            <div>
              <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("clientTour.optional") }}</div>
            </div>
          </div>
          <div class="tooltip-wrapper">
            <v-icon icon="info" size="xs" space="p-4" class="text-secondary" />
            <div class="tooltip">Hello World</div>
          </div>
        </button>
      </h2>

      <div
        id="collapseTourOptional"
        aria-labelledby="headingTourOptional"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionTourOptional"
      >
        <div class="accordion-body pb-16 pb-xl-24">
          <nav class="translations-tab mb-8">
            <div id="nav-tab" class="nav nav-tabs border-bottom-0" role="tablist">
              <v-button
                v-for="(language, index) in languages"
                :id="`nav-template-day-update-basic-information-${language.key}`"
                :key="index"
                :aria-controls="`template-day-update-basic-information-${language.key}`"
                :class="{ active: language.shortName === currentLanguage }"
                :data-bs-target="`#template-day-update-basic-information-${language.key}`"
                aria-selected="true"
                class="nav-tab-link py-4 px-12 me-8 text-xs"
                data-bs-toggle="tab"
                role="tab"
                size="sm"
                variant="outline-primary"
                @click="currentLanguage = language.shortName"
              >
                {{ language.label }}
              </v-button>
            </div>
          </nav>
          <form id="case-tour-update-optional-form" class="d-flex flex-column justify-content-between h-100">
            <fieldset v-for="language in languages" :key="language.shortName">
              <div class="form-outline" v-if="currentLanguage === language.shortName">
                <label class="label text-xs fw-bold text-uppercase text-muted mb-2">
                  <span>{{ $t("clientTour.optional") }}</span>
                </label>
                <v-html-editor
                  :id="'case-tour-update-optional-' + language.shortName"
                  :ref="'editor_optional_' + language.shortName"
                  :value="getDescription(language.shortName)"
                  @input="setDescription(language.shortName, $event)"
                  @media-uploaded="editorMedia.push($event)"
                  height="300"
                />
                <the-template-variable-inserter class="mt-8" @insert="addOptionalInEditor($event)" />
              </div>
            </fieldset>

            <v-button
              :is-loading="isSaving"
              :disabled="isSaving || !isDirty"
              class="btn-icon w-fit align-self-end mt-12"
              size="xs"
              variant="primary"
              @click="updateTour"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="xs" space="ms-8" />
            </v-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { getCurrentInstance, nextTick, ref } from "vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import TheTemplateVariableInserter from "@/components/shared/TheTemplateVariableInserter.vue";
import VHtmlEditor from "@/components/vendor/htmlEditor/VHtmlEditor.vue";
import api from "@/api";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import { useAppLanguages, useLocaleKeyedRef, useLocaleFormDirty } from "@/composables/useAppLanguages";
import ClientTour from "@/api/objects/ClientTour";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  agency: string;
  clientTour: ClientTour;
  caseId: number;
}>();

const emit = defineEmits<{
  (e: "reloadCase"): void;
}>();

const { t } = useI18n();
const toast = useToast();
const { proxy } = getCurrentInstance(); 
const { languages, resolveStoredLocale } = useAppLanguages();
const currentLanguage = ref(resolveStoredLocale(localStorage.getItem("language")));
const isSaving = ref(false);
const editorMedia = ref<any[]>([]);

const optional = useLocaleKeyedRef(() => ({ description: "" }));
const optionalData = props.clientTour?.optional_trip_information?.translations
  || (props.clientTour as any)?.optionalTripInformation?.translations
  || [];

optionalData.forEach((translation: { locale: any; description: any }) => {
  const { locale, description } = translation;
  if (optional.value.hasOwnProperty(locale)) {
    optional.value[locale].description = description;
  }
});

const { isDirty, setDescription, getDescription, markSaved } = useLocaleFormDirty(optional);

const addOptionalInEditor = (text: string) => {
  nextTick(() => {
    const refName = 'editor_optional_' + currentLanguage.value;
    let editorComponent = proxy?.$refs?.[refName];

    if (Array.isArray(editorComponent)) {
      editorComponent = editorComponent[0];
    }
    if (!editorComponent) {
      console.warn('Editor not found:', refName);
      return;
    }
    editorComponent.insertText(text);
  });
};

const updateTour = async () => {
  let saved = false;
  try {
    isSaving.value = true;

    const data = {
      optional_information: optional.value,
    };

    await api.case.updateTranslations(props.agency, props.caseId, data);

    if (editorMedia.value.length > 0) {
      const mediaPayload = {
        media: editorMedia.value,
        model_type: "client_tour",
        collection: "tinymce",
        model_id: props.clientTour.id,
      };
      await api.media.confirm("s3", mediaPayload);
    }

    toast.success(t("general.shared.savedChanges"));
    markSaved();
    saved = true;
  } catch (e: any) {
    console.error(e);
    toast.error(e?.response?.data?.message || "Unknown error");
  } finally {
    isSaving.value = false;
  }

  if (saved) {
    emit("reloadCase");
  }
};
</script>

<style lang="scss" scoped>
.accordion-button,
.accordion-button:not(.collapsed) {
  background-color: transparent;
  padding: 0.5rem 1rem !important;
}

:deep(.form-control),
:deep(.vs__selected),
:deep(.vs__dropdown-option) {
  font-size: 0.8rem !important;
}

:deep(.v-form-label),
:deep(.form-check-label) {
  font-size: 0.75rem !important;
}

:deep(.v-form-input),
:deep(.v-form) {
  margin-bottom: 0.25rem !important;
}

.tooltip-wrapper {
  position: relative;
  display: inline-block;
}

.tooltip {
  position: absolute;
  bottom: 100%; /* Adjust tooltip position */
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--uq-primary);
  color: #fff;
  padding: 5px 10px;
  border-radius: 5px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease-in-out;
}

.tooltip-wrapper:hover .tooltip {
  opacity: 1;
  visibility: visible;
}

.variable-badge {
  font-size: 0.55rem !important;
  padding: 1px 6px !important;
  border-radius: 4px !important;
  height: 25px !important;
  min-height: 25px !important;
  line-height: 1 !important;
  background-color: #E2C92E !important;
  color: #fff !important;
  border: none !important;
}
</style>
